import { useState, useEffect, useMemo } from "react";

const API_URL = "https://peacekeeper.duredemos.com/serviceuat/shopify/orders";

const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseFloat(v));

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

const getCustomerName = (email) => {
  if (!email || email.includes("anonymous")) return "Guest";
  const local = email.split("@")[0];
  return local.split(/[._-]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

const avatarBg = (email) => {
  const palette = ["#6366f1","#0ea5e9","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6"];
  let h = 0;
  for (const c of (email || "?")) h = c.charCodeAt(0) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const initials = (email) => {
  if (!email || email.includes("anonymous")) return "G";
  const parts = email.split("@")[0].split(/[._-]/);
  return parts.length > 1 ? (parts[0][0] + parts[1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase();
};

export default function ShopifyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const PER_PAGE = 10;

  useEffect(() => {
    fetch(API_URL)
      .then((r) => { if (!r.ok) throw new Error("Failed to fetch"); return r.json(); })
      .then((data) => setOrders(Array.isArray(data) ? data : data.data || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => setPage(0), [search]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return orders;
    return orders.filter(
      (o) =>
        o.name?.toLowerCase().includes(q) ||
        getCustomerName(o.email).toLowerCase().includes(q) ||
        o.email?.toLowerCase().includes(q) ||
        o.line_items?.some((i) => i.title?.toLowerCase().includes(q))
    );
  }, [orders, search]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const totalRevenue = orders.reduce((s, o) => s + parseFloat(o.total_price || 0), 0);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        .so-root{font-family:'DM Sans',sans-serif;background:#f5f4f0;min-height:100vh;padding:32px 24px 60px;color:#1a1a1a}

        /* HEADER */
        .so-header{margin-bottom:28px}
        .so-title{font-family:"Roboto", "Helvetica", "Arial", sans-serif;font-weight:600;font-size:2.125rem;letter-spacing:-0.5px;display:flex;align-items:center;gap:10px;color: var(--color-green);
    background: linear-gradient(259.77deg, #029702 0.82%, #045b93, #044e7e 98.09%);
    background-clip: text;
    -webkit-text-fill-color: transparent;}
        .so-subtitle{font-size:0.82rem;color:#999;margin-top:4px}

        /* STATS */
        .so-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:14px;margin-bottom:24px}
        .so-stat{background:#fff;border:1px solid #e8e5df;border-radius:14px;padding:18px 20px}
        .so-stat-label{font-size:0.7rem;color:#aaa;font-weight:500;letter-spacing:0.6px;text-transform:uppercase;margin-bottom:6px}
        .so-stat-value{font-family:'Syne',sans-serif;font-weight:700;font-size:1.5rem;color:#111}

        /* TOOLBAR */
        .so-toolbar{display:flex;align-items:center;gap:12px;margin-bottom:16px;flex-wrap:wrap}
        .so-search-wrap{position:relative;flex:1;max-width:360px}
        .so-search-ico{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#bbb;pointer-events:none}
        .so-search{width:100%;padding:10px 12px 10px 38px;border:1px solid #dedad4;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:0.875rem;background:#fff;color:#111;outline:none;transition:border-color .15s}
        .so-search:focus{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,.1)}
        .so-count{font-size:0.78rem;color:#bbb;margin-left:auto}

        /* CARD */
        .so-card{background:#fff;border:1px solid #e8e5df;border-radius:18px;overflow:hidden}

        /* TABLE */
        .so-table{width:100%;border-collapse:collapse}
        .so-thead tr{background:#faf8f5;border-bottom:1px solid #ede9e3}
        .so-th{padding:12px 16px;font-size:0.68rem;font-weight:600;letter-spacing:0.7px;text-transform:uppercase;color:#aaa;text-align:left;white-space:nowrap}
        .so-th.right{text-align:right}

        .so-row{border-bottom:1px solid #f2eeea;transition:background .12s;cursor:pointer}
        .so-row:last-child{border-bottom:none}
        .so-row:hover{background:#faf8f5}
        .so-row.active{background:#f5f3ff}

        .so-td{padding:13px 16px;font-size:0.85rem;color:#333;vertical-align:middle}
        .so-td.right{text-align:right}

        /* ORDER BADGE */
        .so-badge{display:inline-block;background:#f0f0ee;border-radius:7px;padding:3px 9px;font-family:'Syne',sans-serif;font-weight:700;font-size:0.8rem;color:#555;letter-spacing:0.2px}

        /* CUSTOMER */
        .so-customer{display:flex;align-items:center;gap:9px}
        .so-avatar{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-weight:700;font-size:0.7rem;color:#fff;flex-shrink:0}
        .so-cname{font-weight:500;font-size:0.85rem;color:#1a1a1a}
        .so-cemail{font-size:0.72rem;color:#bbb;margin-top:1px}

        /* PRODUCTS */
        .so-products{display:flex;flex-direction:column;gap:2px}
        .so-prod{font-size:1.2rem;color:#444;display:flex;align-items:center;gap:5px}
        .so-qty{background:#f0f0ee;color:#777;border-radius:5px;padding:1px 5px;font-size:0.7rem;font-weight:600;flex-shrink:0}
        .so-prod-variant{color:#999;font-size:0.90rem}
        .so-more-items{font-size:0.72rem;color:#bbb;margin-top:2px}

        /* DATE */
        .so-datemain{font-weight:500;color:#333}
        .so-datetime{font-size:0.72rem;color:#bbb;margin-top:1px}

        /* PRICE */
        .so-price{font-family:'Syne',sans-serif;font-weight:700;font-size:0.92rem;color:#111}

        /* CHEVRON */
        .so-chev{font-size:0.65rem;color:#ccc;transition:transform .18s,color .18s}
        .so-chev.open{transform:rotate(180deg);color:#6366f1}

        /* DETAIL ROW */
        .so-detail-row{background:#f9f7ff}
        .so-detail-cell{padding:4px 16px 14px 16px}
        .so-detail-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:9px;margin-top:4px}
        .so-item-card{background:#fff;border:1px solid #e8e5df;border-radius:11px;padding:11px 13px}
        .so-item-name{font-weight:500;font-size:0.83rem;color:#1a1a1a}
        .so-item-meta{font-size:0.72rem;color:#aaa;margin-top:5px;display:flex;gap:10px;flex-wrap:wrap}
        .so-ref-tag{color:#6366f1;font-weight:600}

        /* PAGINATION */
        .so-pager{display:flex;align-items:center;justify-content:space-between;padding:12px 18px;border-top:1px solid #f0ede8;background:#faf8f5;flex-wrap:wrap;gap:10px}
        .so-pinfo{font-size:0.77rem;color:#bbb}
        .so-pbtns{display:flex;gap:5px}
        .so-pbtn{width:30px;height:30px;border-radius:7px;border:1px solid #dedad4;background:#fff;cursor:pointer;font-size:0.8rem;color:#555;display:flex;align-items:center;justify-content:center;transition:all .13s;font-family:'DM Sans',sans-serif}
        .so-pbtn:hover:not(:disabled){background:#6366f1;color:#fff;border-color:#6366f1}
        .so-pbtn:disabled{opacity:.3;cursor:not-allowed}
        .so-pbtn.cur{background:#6366f1;color:#fff;border-color:#6366f1;font-weight:600}

        /* STATES */
        .so-state{text-align:center;padding:72px 20px;color:#bbb;font-size:0.875rem}
        .so-spinner{width:32px;height:32px;border:3px solid #eee;border-top-color:#6366f1;border-radius:50%;animation:spin .7s linear infinite;margin:0 auto 14px}
        @keyframes spin{to{transform:rotate(360deg)}}
        .so-empty-ico{font-size:2.2rem;margin-bottom:10px}
      `}</style>

      <div className="so-root">

        {/* Header */}
        <div className="so-header">
          <div className="so-title">
            <span></span>Orders
          </div>
          <div className="so-subtitle">I Am Peacekeeper Store · Order Management</div>
        </div>

        {/* Stats */}
        {!loading && !error && (
          <div className="so-stats">
            <div className="so-stat">
              <div className="so-stat-label">Total Orders</div>
              <div className="so-stat-value">{orders.length}</div>
            </div>
            <div className="so-stat">
              <div className="so-stat-label">Revenue</div>
              <div className="so-stat-value">{fmt(totalRevenue)}</div>
            </div>
            <div className="so-stat">
              <div className="so-stat-label">Avg Order</div>
              <div className="so-stat-value">{orders.length ? fmt(totalRevenue / orders.length) : "$0"}</div>
            </div>
            <div className="so-stat">
              <div className="so-stat-label">Filtered</div>
              <div className="so-stat-value">{filtered.length}</div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        {!loading && !error && (
          <div className="so-toolbar">
            <div className="so-search-wrap">
              <svg className="so-search-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="so-search"
                placeholder="Search order, customer, product…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className="so-count">{filtered.length} orders</span>
          </div>
        )}

        {/* Main Card */}
        <div className="so-card">

          {loading && (
            <div className="so-state">
              <div className="so-spinner" />
              Loading orders…
            </div>
          )}

          {error && (
            <div className="so-state" style={{ color: "#ef4444" }}>⚠️ {error}</div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="so-state">
              <div className="so-empty-ico">🔍</div>
              No orders match your search.
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <table className="so-table">
                <thead className="so-thead">
                  <tr>
                    <th className="so-th">Order</th>
                    <th className="so-th">Customer</th>
                    <th className="so-th">Products</th>
                    <th className="so-th">Date</th>
                    <th className="so-th right">Price</th>
                    <th className="so-th" style={{width:28}}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((order) => {
                    const isOpen = expanded === order.name;
                    const name = getCustomerName(order.email);
                    const bg = avatarBg(order.email || order.name);
                    const ini = initials(order.email || "");
                    const first = order.line_items?.[0];
                    const extra = (order.line_items?.length || 1) - 1;

                    return (
                      <>
                        <tr
                          key={order.name}
                          className={`so-row${isOpen ? " active" : ""}`}
                          onClick={() => setExpanded(isOpen ? null : order.name)}
                        >
                          {/* Order # */}
                          <td className="so-td">
                            <span className="so-badge">{order.name}</span>
                          </td>

                          {/* Customer */}
                          <td className="so-td">
                            <div className="so-customer">
                              <div className="so-avatar" style={{ background: bg }}>{ini}</div>
                              <div>
                                <div className="so-cname">{name}</div>
                                {order.email && !order.email.includes("anonymous") && (
                                  <div className="so-cemail">{order.email}</div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Products */}
                          <td className="so-td">
                            <div className="so-products">
                              {first && (
                                <div className="so-prod">
                                  <span className="so-qty">×{first.quantity}</span>
                                  {first.title}
                                  {first.variant_title && (
                                    <span className="so-prod-variant">· {first.variant_title}</span>
                                  )}
                                </div>
                              )}
                              {extra > 0 && (
                                <div className="so-more-items">+{extra} more item{extra > 1 ? "s" : ""}</div>
                              )}
                            </div>
                          </td>

                          {/* Date */}
                          <td className="so-td">
                            <div className="so-datemain">{fmtDate(order.created_at)}</div>
                            <div className="so-datetime">{fmtTime(order.created_at)}</div>
                          </td>

                          {/* Price */}
                          <td className="so-td right">
                            <span className="so-price">{fmt(order.total_price)}</span>
                          </td>

                          {/* Expand */}
                          <td className="so-td">
                            <span className={`so-chev${isOpen ? " open" : ""}`}>▼</span>
                          </td>
                        </tr>

                        {/* Expanded Items */}
                        {isOpen && (
                          <tr key={`${order.name}-exp`} className="so-detail-row">
                            <td className="so-detail-cell" colSpan={6}>
                              <div className="so-detail-grid">
                                {order.line_items?.map((item) => {
                                  const ref = item.properties?.find((p) => p.name === "Referral Code");
                                  return (
                                    <div key={item.id} className="so-item-card">
                                      <div className="so-item-name">{item.name}</div>
                                      <div className="so-item-meta">
                                        <span>💰 {fmt(item.price)}</span>
                                        <span>📦 Qty {item.quantity}</span>
                                        {item.tax_lines?.[0] && (
                                          <span>🧾 Tax {fmt(item.tax_lines[0].price)}</span>
                                        )}
                                        {/* {ref && (
                                          <span className="so-ref-tag">🏷 {ref.value}</span>
                                        )} */}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="so-pager">
                <span className="so-pinfo">
                  {page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="so-pbtns">
                  <button className="so-pbtn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    const p = totalPages <= 7 ? i : Math.max(0, Math.min(page - 3, totalPages - 7)) + i;
                    return (
                      <button key={p} className={`so-pbtn${p === page ? " cur" : ""}`} onClick={() => setPage(p)}>
                        {p + 1}
                      </button>
                    );
                  })}
                  <button className="so-pbtn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
